"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import checkbox from "@/public/challenge/checkbox.svg";
import { motion } from "framer-motion";
import { CTAButton } from "@/shadcn/ui/button";
import { PortableTextBlock } from "sanity";
import { GROQButton, GROQImage } from "@/sanity/lib/definitions";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function StepsSection({
  // data,
  className,
  chip,
  title,
  steps,
  cta,
}: {
  // data: PageChallengeSchema;
  chip: string;
  title: PortableTextBlock[];
  steps: Array<{
    color: string;
    bgColor: string;
    text: PortableTextBlock[];
    image: GROQImage;
  }>;
  cta: GROQButton;
  className?: string;
}) {
  const isMobile = useIsMobile();
  return (
    <section
      className={cn(
        "relative overflow-clip pb-[7rem] lg:pb-[13rem]",
        className,
      )}
    >
      <div className="container flex flex-col items-start lg:items-center relative">
        <ChipSerif text={chip} className="" />
        {title && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.4 }}
            className="richtext richtext-start lg:richtext-center w-[min(105rem,95%)] prose-p:w-[min(75rem,100%)]"
          >
            <PortableText value={title} components={richTextComponent} />
          </motion.div>
        )}

        <div className="w-[min(214.5rem,100%)] grid md:grid-cols-2 lg:grid-cols-4 gap-[2.5rem] mt-[3rem] lg:mt-[4rem]">
          {steps &&
            steps.length &&
            steps.map((card, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.25,
                  delay: index * 0.08,
                  ease: [0.22, 0.61, 0.36, 1],
                }}
              >
                <div
                  className="h-full flex flex-col rounded-[2rem] lg:rounded-[2.5rem] bg-[#FCFDFD] border border-[#00000005] p-[1.25rem]
                  lg:shadow-[-8px_6px_22px_0_rgba(56,_56,_56,_0.10),_0_-1.257px_5.027px_0_rgba(0,_0,_0,_0.10)_inset]
                  lg:hover:shadow-[-195px_158px_70px_0_rgba(56,_56,_56,_0.00),_-125px_101px_64px_0_rgba(56,_56,_56,_0.03),_-70px_57px_54px_0_rgba(56,_56,_56,_0.10),_0_-1.257px_5.027px_0_rgba(0,_0,_0,_0.10)_inset]
                  transition-shadow duration-300"
                >
                  <div className="relative w-full hidden lg:block">
                    <Image
                      src={card.image}
                      alt=""
                      className="!w-full rounded-[1.5rem]"
                    />
                  </div>
                  <div className="w-full p-[0.75rem] lg:p-[3rem] flex flex-col">
                    <span
                      className="flex justify-center items-center rounded-[1.25rem] w-[7.5rem] h-[5rem] text-[2.25rem] font-[600] mb-[2.15rem]"
                      style={{ color: card.color, background: card.bgColor }}
                    >
                      0{index + 1}
                    </span>
                    {card?.text && (
                      <div
                        className="richtext richtext-start prose-headings:text-[#103037] prose-headings:font-geist prose-headings:text-[2.75rem] lg:prose-headings:mb-0 prose-headings:font-medium prose-headings:tracking-[-0.09rem] [text-wrap-style:pretty]
                        prose-p:text-[1.82888rem] prose-p:text-[#4C4E51]
                        prose-hr:hidden lg:prose-hr:block prose-hr:border-black/[0.15] prose-hr:my-[1.25rem]"
                      >
                        <PortableText
                          value={card.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
        </div>

        <motion.div
          className="mt-[4rem] lg:mt-[6rem] w-full lg:w-fit mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <CTAButton
            data={{ type: isMobile ? "default" : cta.type, cta: cta.cta }}
          />
        </motion.div>
      </div>
    </section>
  );
}
